import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Loader2, PartyPopper } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { renderBadgeCard, inviteUrlFor } from "@/lib/idCard";
import { photoSrc } from "@/components/ProfilePhoto";
import { BADGE_STYLES, BadgePill } from "@/components/Badges";
import { ShareButtons } from "@/components/ShareButtons";

const POLL_MS = 60000;

const fire = (hex) => {
  const colors = [hex, "#E5A93C", "#FFFFFF", "#DC2626", "#B85A32"];
  const shoot = (x, angle) => confetti({ particleCount: 90, spread: 70, startVelocity: 55, angle, origin: { x, y: 0.7 }, colors, zIndex: 9999, scalar: 1.1 });
  shoot(0.1, 60);
  shoot(0.9, 120);
  setTimeout(() => confetti({ particleCount: 160, spread: 120, origin: { y: 0.4 }, colors, zIndex: 9999 }), 350);
};

export const useBadgeUnlock = () => {
  const { user } = useAuth();
  const [unlock, setUnlock] = useState(null);

  useEffect(() => {
    if (!user) return setUnlock(null);
    let alive = true;
    const check = () =>
      api.get("/leaderboard/me").then(({ data }) => {
        if (alive && data.unlocked && data.badge) setUnlock({ badge: data.badge, invites: data.invites });
      }).catch(() => {});
    check();
    const t = setInterval(check, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [user?.talanta_id, user?.invites]);

  return [unlock, () => setUnlock(null)];
};

export const BadgeCelebration = ({ unlock, onClose }) => {
  const { user } = useAuth();
  const [dataUrl, setDataUrl] = useState("");
  const qrWrap = useRef(null);
  const open = !!unlock && !!user;
  const badge = unlock ? { ...unlock.badge, hex: BADGE_STYLES[unlock.badge.id].hex } : null;
  const inviteUrl = user ? inviteUrlFor(user.talanta_id) : "";

  useEffect(() => {
    if (!open) return;
    setDataUrl("");
    fire(badge.hex);
    const t = setTimeout(async () => {
      try {
        setDataUrl(await renderBadgeCard({ user: { ...user, invites: unlock.invites }, badge, qrCanvas: qrWrap.current?.querySelector("canvas"), photoUrl: photoSrc(user) }));
      } catch (e) { /* preview optional */ }
    }, 80);
    return () => clearTimeout(t);
  }, [open]);

  const close = async () => {
    try { await api.post("/leaderboard/me/badge-seen", { badge_id: badge.id }); } catch (e) { /* ignore */ }
    onClose();
  };

  if (!open) return null;
  const text = `I just earned the ${badge.label} inviter badge on Talanta Hela — ${unlock.invites} friends joined Kenya's talent economy through my One Talanta ID (${user.talanta_id}). Join me:`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="badge-celebration-dialog" className="sm:max-w-md p-0 overflow-hidden border-0 bg-talanta-green text-white max-h-[92vh] overflow-y-auto">
        <DialogTitle className="sr-only">Badge unlocked</DialogTitle>
        <DialogDescription className="sr-only">You earned a new inviter badge. Share it with friends.</DialogDescription>
        <div className="kenya-stripe h-1.5" />
        <div className="px-5 pt-6 text-center">
          <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 14 }} className="mx-auto h-20 w-20 rounded-full flex items-center justify-center shadow-2xl" style={{ background: badge.hex, boxShadow: `0 0 60px ${badge.hex}88` }}>
            <PartyPopper className="h-9 w-9 text-white" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-4 text-talanta-gold text-xs font-semibold uppercase tracking-[0.25em]">Badge unlocked</motion.div>
          <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} data-testid="badge-celebration-title" className="mt-2 font-display font-extrabold text-3xl">
            You just earned {badge.label}!
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="mt-2 text-sm text-white/70">
            {unlock.invites} friend{unlock.invites === 1 ? "" : "s"} joined through your One Talanta ID. <BadgePill badge={badge} dark />
          </motion.p>
        </div>
        <div className="px-5 pt-5">
          <div className="aspect-square w-full rounded-2xl overflow-hidden bg-talanta-hover shadow-xl shadow-black/30 flex items-center justify-center">
            {dataUrl ? <img data-testid="badge-card-preview" src={dataUrl} alt={`I just earned ${badge.label}`} className="h-full w-full object-cover" /> : <Loader2 className="h-8 w-8 text-talanta-gold animate-spin" />}
          </div>
          <div ref={qrWrap} className="hidden"><QRCodeCanvas value={inviteUrl} size={384} level="M" fgColor="#083323" bgColor="#FFFFFF" /></div>
        </div>
        <div className="px-5 py-5 space-y-3">
          <ShareButtons dataUrl={dataUrl} fileName={`talanta-badge-${badge.id}-${user.talanta_id}.png`} text={text} url={inviteUrl} title={`I just earned ${badge.label} on Talanta Hela`} idPrefix="badge-share" />
          <button data-testid="badge-celebration-close" onClick={close} className="w-full text-sm text-white/60 hover:text-white py-1">Keep climbing →</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
